import React, {FormEvent, useCallback} from 'react';
import { ReferralFilterActionPayload } from './action';
import Popper from 'popper.js';
import { ReferralFilterState } from './state';
import FilterListIcon from '@material-ui/icons/FilterList';
import { AutoCompleteMultiInput, DropdownMultiSelect } from 'common';
import { scopeOfServiceMapping, PlacementType, serviceTypeMapping, ServiceType } from 'referral';
import {ServiceLocationEnumNameToValue, SPLIT_DELIMITER} from "../../placement/const";
import {BehaviorSubject} from "rxjs";

export type ReferralFilterOptions = {
    scopeOfServices: Array<string>;
    serviceTypes: Array<string>;
    serviceLocationTypes: Array<string>;
}

type Props = {
    filter$: BehaviorSubject<ReferralFilterState>,
    filterOption: ReferralFilterOptions,
    dispatch: React.Dispatch<ReferralFilterActionPayload>
}

function splitAndRemoveEmpty(val: string): string[] {
    return val
            .split(SPLIT_DELIMITER)
            .map((val) => val.trim())
            .filter((val) => val !== '');
}

export function ReferralFilter({dispatch, filter$, filterOption }: Props) {
    const [showReferralFilterDropdown, setShowReferralFilterDropdown] = React.useState(false);
    const btnDropdownRef = React.useRef(null);
    const dropdownRef = React.useRef(null);
    const nodeRef = React.useRef(null);

    const handleClickOutside = (e: Event) => {
        if ((nodeRef.current as any).contains(e.target)) {
            // outside
            return;
        }
        setShowReferralFilterDropdown(false);
    };

    // internal filter state
    const [filterValue, setInternalFilterValue] = React.useState({...filter$.getValue()});
    const setFilterValue = useCallback((value) => {
        setInternalFilterValue(value);
        filter$.next(value);
    }, [setInternalFilterValue, filter$]);

    // Sync external filter$ changes to internal state
    React.useEffect(() => {
        const subscription = filter$.subscribe((newFilterValue) => {
            setInternalFilterValue({...newFilterValue});
        });
        return () => subscription.unsubscribe();
    }, [filter$]);

    const openDropdown = () => {
        // for sonar cloud (this constructor use to set position of the dropdown)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const _ = new Popper(btnDropdownRef.current!, dropdownRef.current!, {
            placement: "bottom"
        });
        setShowReferralFilterDropdown(true);
    };

    const closeDropdown = () => {
        setShowReferralFilterDropdown(false);
    };

    React.useEffect(() => {
        if (showReferralFilterDropdown) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showReferralFilterDropdown]);


    const onSubmit = (e: FormEvent) => {
        e.preventDefault();
        // set filter state for the page
        dispatch({type: 'REFERRAL_FILTER_ACTION', filter: filterValue});
        setShowReferralFilterDropdown(false);
    };

    const reset = () => {

        // set callback filter value
        setFilterValue({
            counties: [],
            scopeOfServices: [],
            zipcodes: [],
            cities: [],
            serviceTypes: [],
            serviceLocationTypes: [],
        });

        dispatch({type: 'REFERRAL_FILTER_ACTION', filter: {
            counties: [],
            scopeOfServices: [],
            zipcodes: [],
            cities: [],
            serviceTypes: [],
            serviceLocationTypes: [],
        }});

        setShowReferralFilterDropdown(false);
    }
    const bgColor = showReferralFilterDropdown ||
                    filterValue.counties.length > 0 ||
                    filterValue.scopeOfServices.length > 0 ||
                    filterValue.zipcodes.length > 0 ||
                    filterValue.cities.length > 0 ||
                    filterValue.serviceLocationTypes.length > 0 ||
                    filterValue.serviceTypes.length > 0 ? 'bg-atlantis-200' : ''

    const onClickShowReferralFilter = () => {
        showReferralFilterDropdown
        ? closeDropdown()
        : openDropdown();
    }

    return (<div ref={nodeRef} className='px-5 py'>
        <button
            className={`py-2 px-4 focus:bg-atlantis-200 hover:bg-atlantis-200 rounded ${bgColor}`}
            onClick={onClickShowReferralFilter}
            ref={btnDropdownRef}>
            <FilterListIcon />
        </button>
        <form onSubmit={onSubmit}>
            <div
                ref={dropdownRef}
                className={
                    (showReferralFilterDropdown ? "block " : "hidden ") +
                    "p-5 bg-white text-black text-base z-50 float-left py-2 list-none text-left rounded shadow-lg mt-1"
                }
                style={{ minWidth: "550px" }}
            >
                <div className='text-lg font-medium'>
                    Filters
                </div>
                <div className='pt-5 font-medium'>
                    <div className='pb-2'>
                        Scope of Services
                    </div>
                    <DropdownMultiSelect
                        field={'scopeOfServices'}
                        options={filterOption.scopeOfServices}
                        placeholder={'— — Choose Services — —'}
                        full
                        value={filterValue.scopeOfServices.join(SPLIT_DELIMITER)}
                        setValue={(val) => setFilterValue({ ...filterValue, scopeOfServices: splitAndRemoveEmpty(val) })}
                        labelMapperFunc={(val: string) => scopeOfServiceMapping[val as PlacementType]}
                    />
                </div>
                <div className='pt-5 font-medium'>
                    <div className='pb-2'>
                        Service Types
                    </div>
                    <DropdownMultiSelect
                        field={'serviceTypes'}
                        options={filterOption.serviceTypes}
                        placeholder={'— — Choose Service Types — — '}
                        full
                        value={filterValue.serviceTypes.join(SPLIT_DELIMITER)}
                        setValue={(val) => setFilterValue({ ...filterValue, serviceTypes: splitAndRemoveEmpty(val) })}
                        labelMapperFunc={(val: string) => serviceTypeMapping[val as ServiceType]}
                    />
                </div>
                <div className='pt-5 font-medium'>
                        <div className='pb-2'>
                            Service Location
                        </div>
                        <DropdownMultiSelect
                            field={'serviceLocationTypes'}
                            options={filterOption.serviceLocationTypes}
                            labelMapperFunc={(name) => ServiceLocationEnumNameToValue.get(name) ?? ""}
                            placeholder={'— — Choose Service Location — — '}
                            full
                            value={filterValue.serviceLocationTypes.join(SPLIT_DELIMITER)}
                            setValue={(val) => setFilterValue({...filterValue, serviceLocationTypes: splitAndRemoveEmpty(val)})}
                        />
                </div>
                <div className='pt-5 font-medium'>
                    <div className='pb-2'>
                        County
                    </div>
                    <AutoCompleteMultiInput field={'county'}
                                            fieldValue={filterValue.counties.join(SPLIT_DELIMITER)}
                                            queryType='county'
                                            inputType='char-only'
                                            setFieldValue={(val) => setFilterValue({ ...filterValue, counties: splitAndRemoveEmpty(val) })}/>
                </div>
                <div className='pt-5 font-medium'>
                    <div className='pb-2'>
                        City
                    </div>
                    <AutoCompleteMultiInput field={'city'}
                                            fieldValue={filterValue.cities.join(SPLIT_DELIMITER)}
                                            queryType='city'
                                            inputType='char-only'
                                            setFieldValue={(val) => setFilterValue({ ...filterValue, cities: splitAndRemoveEmpty(val) })}/>
                </div>
                <div className='pt-5 font-medium'>
                    <div className='pb-2'>
                        Zip Code
                    </div>
                    <AutoCompleteMultiInput field={'zipcode'}
                                            fieldValue={filterValue.zipcodes.join(SPLIT_DELIMITER)}
                                            queryType='city'
                                            inputType='number'
                                            setFieldValue={(val) => setFilterValue({ ...filterValue, zipcodes: splitAndRemoveEmpty(val) })}/>
                </div>
                <div className='pt-10 pb-5 flex flex-row-reverse'>
                    <div className='pl-5'>
                        <button type='submit' className='bg-atlantis-200 font-light hover:bg-atlantis-300 text-white py-2 px-4 rounded' style={{ minWidth: '82px' }}>
                            Apply
                        </button>
                    </div>

                    <button type='button' onClick={() => reset()} className='bg-gray-300 font-light hover:bg-gray-500 text-black py-2 px-4 rounded' style={{ minWidth: '82px' }}>
                        Reset
                    </button>
                </div>
            </div>
        </form>
    </div>
    );
}
